<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Repasses</h1>
      <button v-if="authStore.canManageRepasses" class="btn-primary" @click="openNew()">+ Registrar repasse</button>
    </div>

    <p class="page-help">
      Quanto cada evento ainda precisa receber. O sistema desconta automaticamente os repasses ja registrados.
    </p>

    <SkeletonLoader v-if="loading && !resumo.length" variant="row" :count="5" />

    <div v-else-if="resumo.length" class="card table-card">
      <div class="table-wrap"><table class="table">
        <thead>
          <tr>
            <th>Evento</th>
            <th class="right">Bruto</th>
            <th class="right">Comissao</th>
            <th class="right">Total devido</th>
            <th class="right">Ja repassado</th>
            <th class="right">Saldo</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in resumo" :key="e.id" :class="{ devendo: e.saldoARepassar > 0.01 }">
            <td>
              <strong>{{ e.nome }}</strong><br>
              <small>/{{ e.slug }}</small>
            </td>
            <td class="right">{{ formatMoney(e.bruto) }}</td>
            <td class="right muted">- {{ formatMoney(e.comissaoPlataforma) }}</td>
            <td class="right">{{ formatMoney(e.aRepassarBruto) }}</td>
            <td class="right muted">- {{ formatMoney(e.totalRepassado) }}</td>
            <td class="right saldo">{{ formatMoney(e.saldoARepassar) }}</td>
            <td>
              <button v-if="authStore.canManageRepasses && e.saldoARepassar > 0.01" class="btn-secondary btn-sm" @click="openNew(e)">
                Registrar
              </button>
            </td>
          </tr>
        </tbody>
      </table></div>
    </div>

    <EmptyState v-else icon="💸" title="Sem dados" description="Crie eventos com inscricoes pagas pra registrar repasses." />

    <div v-if="items.length" class="card historico-card">
      <h2 class="card-title">Historico de repasses</h2>
      <div class="table-wrap"><table class="table">
        <thead>
          <tr><th>Data</th><th>Evento</th><th class="right">Valor</th><th>Metodo</th><th>Por</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="r in items" :key="r.id">
            <td>{{ formatDate(r.dataTransferencia) }}</td>
            <td>{{ r.evento?.nome }}</td>
            <td class="right">{{ formatMoney(r.valor) }}</td>
            <td>{{ r.metodoTransferencia || '—' }}</td>
            <td><small>{{ r.registradoPor?.name }}</small></td>
            <td>
              <a v-if="r.comprovanteUrl" :href="r.comprovanteUrl" target="_blank" class="link-btn">Comprovante</a>
              <button v-if="authStore.canManageRepasses" class="link-btn link-danger" @click="confirmRemove(r)">Excluir</button>
            </td>
          </tr>
        </tbody>
      </table></div>
    </div>

    <!-- New repasse modal -->
    <div v-if="showNew" class="modal-overlay" @click.self="closeNew">
      <div class="modal-card">
        <h2 class="modal-title">Registrar repasse</h2>
        <form @submit.prevent="handleCreate">
          <div class="form-row">
            <label class="label">Evento</label>
            <select class="select" v-model="form.eventoId" required>
              <option value="" disabled>Selecione</option>
              <option v-for="e in resumo" :key="e.id" :value="e.id">
                {{ e.nome }} — saldo {{ formatMoney(e.saldoARepassar) }}
              </option>
            </select>
          </div>
          <div class="form-grid">
            <div class="form-row">
              <label class="label">Valor (R$)</label>
              <input class="input" type="number" min="0" step="0.01" v-model.number="form.valor" required />
            </div>
            <div class="form-row">
              <label class="label">Data</label>
              <input class="input" type="date" v-model="form.dataTransferencia" required />
            </div>
          </div>
          <div class="form-grid">
            <div class="form-row">
              <label class="label">Metodo</label>
              <input class="input" v-model="form.metodoTransferencia" placeholder="PIX, TED, dinheiro..." maxlength="60" />
            </div>
            <div class="form-row">
              <label class="label">Comprovante (URL, opcional)</label>
              <input class="input" v-model="form.comprovanteUrl" placeholder="https://..." />
            </div>
          </div>
          <div class="form-row">
            <label class="label">Observacao</label>
            <textarea class="textarea" v-model="form.observacao" rows="2" maxlength="2000"></textarea>
          </div>
          <div v-if="createError" class="error-msg">{{ createError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeNew">Cancelar</button>
            <button type="submit" class="btn-primary" :disabled="creating">{{ creating ? 'Salvando...' : 'Registrar' }}</button>
          </div>
        </form>
      </div>
    </div>

    <ConfirmDialog
      :visible="removeTarget !== null"
      title="Excluir repasse?"
      message="Esta acao recalcula o saldo e nao pode ser desfeita."
      confirmLabel="Excluir"
      @confirm="handleRemove"
      @cancel="removeTarget = null"
    />
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useRepassesStore } from '../../stores/repasses'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import ConfirmDialog from '../../components/ui/ConfirmDialog.vue'

function todayYmd() {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function emptyForm() {
  return { eventoId: '', valor: 0, dataTransferencia: todayYmd(), metodoTransferencia: 'PIX', comprovanteUrl: '', observacao: '' }
}

export default {
  name: 'RepassesPage',
  components: { SkeletonLoader, EmptyState, ConfirmDialog },
  data() {
    return {
      showNew: false,
      form: emptyForm(),
      creating: false,
      createError: '',
      removeTarget: null
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    store() { return useRepassesStore() },
    loading() { return this.store.loading },
    resumo() { return this.store.resumo },
    items() { return this.store.items }
  },
  async mounted() {
    await Promise.all([this.store.fetchResumo(), this.store.fetchAll()])
  },
  methods: {
    formatMoney(v) { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0) },
    formatDate(d) { try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d } },
    openNew(evento) {
      this.form = emptyForm()
      if (evento) {
        this.form.eventoId = evento.id
        this.form.valor = evento.saldoARepassar > 0 ? Math.round(evento.saldoARepassar * 100) / 100 : 0
      }
      this.createError = ''
      this.showNew = true
    },
    closeNew() { this.showNew = false },
    async handleCreate() {
      this.creating = true
      this.createError = ''
      try {
        await this.store.registrar({
          eventoId: this.form.eventoId,
          valor: Number(this.form.valor),
          dataTransferencia: this.form.dataTransferencia,
          metodoTransferencia: this.form.metodoTransferencia || null,
          comprovanteUrl: this.form.comprovanteUrl || null,
          observacao: this.form.observacao || null
        })
        await this.store.fetchResumo()
        this.$toast.open({ message: 'Repasse registrado', type: 'success', position: 'top-right' })
        this.closeNew()
      } catch (err) {
        const data = err.response?.data
        let msg = data?.message || 'Erro ao registrar'
        if (Array.isArray(data?.errors) && data.errors.length) {
          msg += ': ' + data.errors.map(e => `${e.field} - ${e.message}`).join('; ')
        }
        this.createError = msg
      } finally { this.creating = false }
    },
    confirmRemove(r) { this.removeTarget = r },
    async handleRemove() {
      const r = this.removeTarget
      this.removeTarget = null
      try {
        await this.store.remover(r.id)
        await this.store.fetchResumo()
        this.$toast.open({ message: 'Repasse removido', type: 'info', position: 'top-right' })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    }
  }
}
</script>

<style scoped>
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--spacing-base); }
.page-header .page-title { margin-bottom: 0; }
.page-help { font-size: var(--text-sm); color: var(--color-text-muted); margin-bottom: var(--spacing-lg); }
.table-card, .historico-card { padding: 0; overflow-x: auto; margin-bottom: var(--spacing-lg); }
.historico-card { padding: var(--spacing-lg); }
.card-title { font-size: var(--text-md); margin-bottom: var(--spacing-base); }
.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 10px var(--spacing-base);
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table th.right, .table td.right { text-align: right; }
.table td {
  padding: 12px var(--spacing-base);
  font-size: var(--text-sm);
  border-bottom: 1px solid var(--color-bg-separator);
}
.devendo .saldo { color: var(--color-danger); font-weight: 700; }
.muted { color: var(--color-text-muted); }
small { color: var(--color-text-muted); }
.btn-sm { padding: 6px 12px; font-size: var(--text-xs); }
.form-row { margin-bottom: var(--spacing-md); display: flex; flex-direction: column; gap: 4px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); }
@media (max-width: 540px) {
  .form-grid { grid-template-columns: 1fr; }
}
.link-btn { background: none; border: none; cursor: pointer; color: var(--color-accent); font-size: var(--text-xs); text-decoration: none; padding: 4px 6px; }
.link-danger { color: var(--color-danger); }
</style>
